import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import api from "@helpers/api";
import { useCommunity } from "@contexts/CommunityContext";

import { DEFAULT_PLAN_TAB, PLAN_TABS, isPlanArchived } from "../enums/planTabs";

const VALID_TABS = new Set(Object.values(PLAN_TABS));

export default function usePricing() {
  const { t } = useTranslation("settings");
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    loading: communityLoading,
    community,
    loadCommunities,
  } = useCommunity();

  const currentCommunity = community.getBySlug(slug);
  const communityId = currentCommunity?.id;

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sharePlan, setSharePlan] = useState(null);

  const requestRef = useRef(0);
  const hasLoadedRef = useRef(false);

  // The active tab lives in the URL (?tab=archived) so it survives a refresh
  // and browser back/forward; the default tab needs no param.
  const tabParam = searchParams.get("tab");
  const activeTab = VALID_TABS.has(tabParam) ? tabParam : DEFAULT_PLAN_TAB;

  const load = useCallback(async () => {
    if (!communityId) {
      setLoading(false);
      return;
    }

    const requestId = ++requestRef.current;
    // Only the first load shows the skeleton; later refreshes (after an
    // archive, a save, a community reload) swap the rows in place.
    if (!hasLoadedRef.current) setLoading(true);

    // The owner is always the viewer here, so archived plans are requested
    // too and split client-side. api.js resolves HTTP errors by returning the
    // axios error, so a failure shows up as a non-array `data`.
    const response = await api.get(`community/${communityId}/plan`, {
      include_disabled: true,
    });

    if (requestId !== requestRef.current) return;

    if (!Array.isArray(response?.data)) {
      console.error(response);
      setPlans([]);
    } else {
      setPlans(response.data);
      hasLoadedRef.current = true;
    }

    setLoading(false);
  }, [communityId]);

  useEffect(() => {
    if (!communityLoading) load();
  }, [communityLoading, load]);

  const { activePlans, archivedPlans } = useMemo(
    () => ({
      activePlans: plans.filter((plan) => !isPlanArchived(plan)),
      archivedPlans: plans.filter(isPlanArchived),
    }),
    [plans],
  );

  const visiblePlans =
    activeTab === PLAN_TABS.ARCHIVED ? archivedPlans : activePlans;

  const counts = {
    [PLAN_TABS.ACTIVE]: activePlans.length,
    [PLAN_TABS.ARCHIVED]: archivedPlans.length,
  };

  const changeTab = useCallback(
    (tab) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (!tab || tab === DEFAULT_PLAN_TAB) next.delete("tab");
          else next.set("tab", tab);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Replaces (or prepends) a plan returned by the API so the table reflects
  // the change without a full reload.
  const applyPlanUpdate = useCallback((plan) => {
    if (!plan?.id) return;
    setPlans((prev) =>
      prev.some((item) => item.id === plan.id)
        ? prev.map((item) => (item.id === plan.id ? plan : item))
        : [plan, ...prev],
    );
  }, []);

  // Archive and reactivate share one PUT. `enabled` is the only field sent,
  // so the backend keeps everything else (price lock included) untouched.
  const setPlanEnabled = useCallback(
    async (planId, enabled) => {
      const response = await api.put(`community/plan/${planId}`, { enabled });
      if (!response?.data?.id) throw response;
      applyPlanUpdate(response.data);
      return response.data;
    },
    [applyPlanUpdate],
  );

  // The community context caches the plans shown on the home screen; any
  // change in plan visibility must reach it too.
  const syncCommunities = useCallback(async () => {
    try {
      await loadCommunities();
    } catch (e) {
      console.error(e);
    }
  }, [loadCommunities]);

  const archivePlan = useCallback(
    (plan) => {
      window.openConfirm({
        title: t("pricing.archive.title"),
        headline: t("shared.warning"),
        subtitle: t("pricing.archive.subtitle"),
        buttons: [
          {
            text: t("pricing.archive.confirm"),
            variant: "contained",
            onClick: async () => {
              try {
                await setPlanEnabled(plan.id, false);
                window.setSnackbar(t("pricing.archive.success"));
                syncCommunities();
              } catch (e) {
                console.error(e);
                window.setSnackbar(t("pricing.archive.error"));
              }
            },
          },
          {
            text: t("pricing.archive.keep"),
            variant: "text",
          },
        ],
      });
    },
    [t, setPlanEnabled, syncCommunities],
  );

  const reactivatePlan = useCallback(
    async (plan) => {
      try {
        await setPlanEnabled(plan.id, true);
        window.setSnackbar(t("pricing.reactivate.success"));
        syncCommunities();
      } catch (e) {
        console.error(e);
        window.setSnackbar(t("pricing.reactivate.error"));
      }
    },
    [t, setPlanEnabled, syncCommunities],
  );

  const deletePlan = useCallback(
    (plan) => {
      window.openConfirm({
        title: t("pricing.delete.title"),
        headline: t("shared.warning"),
        subtitle: t("pricing.delete.subtitle"),
        buttons: [
          {
            text: t("shared.deletePermanently"),
            variant: "contained",
            color: "error",
            onClick: async () => {
              const response = await api.delete(`community/plan/${plan.id}`);

              if (response?.status === 204) {
                window.setSnackbar(t("pricing.delete.success"));
                setPlans((prev) => prev.filter((item) => item.id !== plan.id));
                syncCommunities();
                return;
              }

              console.error(response);
              window.setSnackbar(t("pricing.delete.error"));
            },
          },
          {
            text: t("pricing.delete.keep"),
            variant: "text",
          },
        ],
      });
    },
    [t, syncCommunities],
  );

  const copyCheckoutLink = useCallback(
    (plan) => {
      const link = `https://pay.ginastee.com/${currentCommunity?.checkout}?plan=${plan.id}`;

      navigator.clipboard
        .writeText(link)
        .then(() => window.setSnackbar(t("shared.linkCopied")))
        .catch((err) => {
          console.error(err);
          window.setSnackbar(t("shared.copyLinkError"));
        });
    },
    [currentCommunity?.checkout, t],
  );

  return {
    currentCommunity,
    communityId,
    communityLoading,
    plans: visiblePlans,
    counts,
    loading,
    activeTab,
    changeTab,
    sharePlan,
    setSharePlan,
    load,
    applyPlanUpdate,
    syncCommunities,
    archivePlan,
    reactivatePlan,
    deletePlan,
    copyCheckoutLink,
  };
}
